import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/queries/query-keys";

export interface WorkExperience {
  id: number;
  company: string;
  role: string;
  period: string;
  description: string | null;
  url: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateExperienceInput {
  company: string;
  role: string;
  period: string;
  description?: string;
  url?: string;
  display_order?: number;
}

// Fetch all experience (public)
export function useExperience() {
  return useQuery({
    queryKey: queryKeys.experience.all,
    queryFn: async (): Promise<WorkExperience[]> => {
      const response = await fetch("/api/experience");
      if (!response.ok) {
        throw new Error("Failed to fetch experience");
      }
      const data = await response.json();
      return data.experience;
    },
  });
}

// Fetch all experience (admin)
export function useAdminExperience() {
  return useQuery({
    queryKey: queryKeys.experience.admin,
    queryFn: async (): Promise<WorkExperience[]> => {
      const response = await fetch("/api/admin/experience");
      if (!response.ok) {
        throw new Error("Failed to fetch experience");
      }
      const data = await response.json();
      return data.experience;
    },
  });
}

// Create experience
export function useCreateExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateExperienceInput) => {
      const response = await fetch("/api/admin/experience", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error("Failed to create experience");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.admin });
    },
  });
}

// Update experience
export function useUpdateExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: CreateExperienceInput & { id: number }) => {
      const response = await fetch(`/api/admin/experience/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error("Failed to update experience");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.admin });
    },
  });
}

// Delete experience
export function useDeleteExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/admin/experience/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete experience");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.admin });
    },
  });
}

// Reorder experience
export function useReorderExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (order: number[]) => {
      const response = await fetch("/api/admin/experience/reorder", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order }),
      });
      if (!response.ok) {
        throw new Error("Failed to reorder experience");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.experience.admin });
    },
  });
}
